import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Card, Button, Form, Modal } from 'react-bootstrap';
import ProductMediaGallery from '../components/ProductMediaGallery';
import VariantPicker from '../components/VariantPicker';
import { normalizeImageUrl } from '../utils/urlSanitizer';
import './ProductScreen.css';
import { Editor, EditorState, ContentState } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import Rating from '../components/Rating';
import Message from '../components/Message';
import Loader from '../components/Loader';
import Meta from '../components/Meta';
import { Helmet } from 'react-helmet';
import CustomerPhotoGallery from '../components/CustomerPhotoGallery';

import {
  listProductDetails,
  listProductDetailsBySlug,
  createProductReview,
  adminCreateProductReview,
  adminUpdateProductReview,
  adminDeleteProductReview,
  adminBulkDeleteProductReviews,
  resetCreateReview,
  resetAdminReviewOp,
} from '../slices/productSlice';
import { addToCart } from '../slices/cartSlice';
// import { PRODUCT_CREATE_REVIEW_RESET } from '../constants/productConstants';

const ProductScreen = ({ history, match }) => {
  const [showComponent, setShowComponent] = useState(false);
  // Local, guarded product used for rendering to avoid race conditions
  const [productView, setProductView] = useState(null);
  const [descriptionState, setDescriptionState] = useState(() => EditorState.createEmpty());
  const [qty, setQty] = useState(1);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [, setProductMedia] = useState([]);
  const dispatch = useDispatch();

  // Themed modals for prompting variant selection or unavailable notice
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [showAddedToast, setShowAddedToast] = useState(false);
  const [showReviewPurchaseModal, setShowReviewPurchaseModal] = useState(false);

  const [showAllReviewsModal, setShowAllReviewsModal] = useState(false);
  const [showReviewImageViewer, setShowReviewImageViewer] = useState(false);
  const [reviewViewerImages, setReviewViewerImages] = useState([]);
  const [reviewViewerIndex, setReviewViewerIndex] = useState(0);
  const [reviewsRatingFilter, setReviewsRatingFilter] = useState('all');
  const [selectedReviewIds, setSelectedReviewIds] = useState({});
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [editReviewName, setEditReviewName] = useState('');
  const [editReviewRating, setEditReviewRating] = useState(5);
  const [editReviewComment, setEditReviewComment] = useState('');
  const [editReviewImages, setEditReviewImages] = useState([]);
  const [editReviewVariant, setEditReviewVariant] = useState('');
  const [newReviewName, setNewReviewName] = useState('');
  const [newReviewRating, setNewReviewRating] = useState(5);
  const [newReviewComment, setNewReviewComment] = useState('');
  const [newReviewImages, setNewReviewImages] = useState([]);
  const [newReviewVariant, setNewReviewVariant] = useState('');


  const productDetails = useSelector((state) => state.productList);
  const {
    loadingDetails: loading,
    errorDetails: error,
    productDetails: product,
    successReview,
    loadingReview,
  } = productDetails;

  const loadingAdminReviewOp = productDetails?.loadingAdminReviewOp;
  const successAdminReviewOp = productDetails?.successAdminReviewOp;
  const errorAdminReviewOp = productDetails?.errorAdminReviewOp;

  // Keep existing variable names used below by aliasing slice fields
  const successProductReview = successReview;
  const loadingProductReview = loadingReview;
  const errorProductReview = productDetails?.error;

  const REVIEW_PURCHASE_REQUIRED = 'Please purchase this product to leave a review';
  const normalizedReviewError = useMemo(() => {
    if (!errorProductReview) return '';
    const raw = typeof errorProductReview === 'string'
      ? errorProductReview
      : (errorProductReview?.message || '');
    return String(raw)
      .split('\n')[0]
      .replace(/^Error:\s*/i, '')
      .trim();
  }, [errorProductReview]);

  useEffect(() => {
    if (normalizedReviewError === REVIEW_PURCHASE_REQUIRED) {
      setShowReviewPurchaseModal(true);
    }
  }, [normalizedReviewError]);

  const [selectedColorsVariants, setSelectedColorsVariants] = useState('');
  const [selectedAttrs, setSelectedAttrs] = useState({});
  const [selectedSku, setSelectedSku] = useState(null);
  // Track whether the user has actively interacted with variant controls; gates image swaps
  const [userVariantInteracted, setUserVariantInteracted] = useState(false);


  const userInfo = useSelector((state) => state.user?.userInfo);
  const isAdmin = !!userInfo?.isAdmin;

  // Normalize purchase link for admins (adds https:// if missing)
  const safePurchaseLink = useMemo(() => {
    const raw = (productView?.purchaseLink || '').toString().trim();
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return raw;
    if (/^\/\//.test(raw)) return `https:${raw}`;
    return `https://${raw.replace(/^\/+/, '')}`;
  }, [productView?.purchaseLink]);

  // Refs to guard effects
  const fetchedForIdRef = useRef(null);
  const initializedForIdRef = useRef(null);
  const currentKeyRef = useRef(match.params.id);
  const reviewsSectionRef = useRef(null);

  // Review image viewer touch-swipe tracking
  const reviewTouchStartXRef = useRef(null);
  const reviewTouchStartYRef = useRef(null);
  const reviewTouchActiveRef = useRef(false);

  const scrollToReviews = useCallback(() => {
    reviewsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Track latest route key
  useEffect(() => {
    currentKeyRef.current = match.params.id;
  }, [match.params.id]);

  // Compute display price/availability based on selected SKU or legacy variant
  const hasNewVariants = Array.isArray(productView?.variantProps) && productView.variantProps.length > 0 && Array.isArray(productView?.skus)
  const selectedVariant = !hasNewVariants && Array.isArray(productView?.colorsVariants)
    ? productView.colorsVariants.find((v) => v?.name === selectedColorsVariants)
    : undefined;
  const basePrice = Number(productView?.price)
  // Treat value-level disabled as unavailable too
  const anySelectedValueDisabled = hasNewVariants && Array.isArray(productView?.variantProps)
    ? productView.variantProps.some(p => {
        const vid = (selectedAttrs || {})[p.id]
        if (!vid) return false
        const valObj = (p.values || []).find(v => v && v.id === vid)
        return !!valObj?.disabled
      })
    : false
  const isUnavailable = hasNewVariants ? (!!selectedSku?.unavailable || anySelectedValueDisabled) : !!selectedVariant?.unavailable
  const displayPrice = Number(
    hasNewVariants
      ? (selectedSku && selectedSku.price !== undefined && selectedSku.price !== null ? Number(selectedSku.price) : basePrice)
      : ((selectedVariant && typeof selectedVariant.price === 'number' && !Number.isNaN(selectedVariant.price)) ? selectedVariant.price : basePrice)
  )
  const formattedPrice = Number.isFinite(displayPrice) ? displayPrice.toFixed(2) : '0.00'

  // Order reviews by date (newest first)
  const orderedReviews = useMemo(() => {
    const list = Array.isArray(productView?.reviews) ? productView.reviews : [];
    return [...list].sort((a, b) => {
      const ta = Date.parse(a?.createdAt);
      const tb = Date.parse(b?.createdAt);
      const va = Number.isFinite(ta) ? ta : Number.NEGATIVE_INFINITY;
      const vb = Number.isFinite(tb) ? tb : Number.NEGATIVE_INFINITY;
      if (va !== vb) return vb - va;
      const ida = String(a?._id || '');
      const idb = String(b?._id || '');
      return ida.localeCompare(idb);
    });
  }, [productView?.reviews]);

  const modalReviews = useMemo(() => {
    const filter = String(reviewsRatingFilter || 'all');
    if (filter === 'all') return orderedReviews;
    const ratingNum = Number(filter);
    if (!Number.isFinite(ratingNum)) return orderedReviews;
    return orderedReviews.filter((r) => Number(r?.rating) === ratingNum);
  }, [orderedReviews, reviewsRatingFilter]);

  const selectedIds = useMemo(() => {
    const out = [];
    const map = selectedReviewIds || {};
    for (const [id, v] of Object.entries(map)) {
      if (v) out.push(id);
    }
    return out;
  }, [selectedReviewIds]);

  const allVisibleSelected = useMemo(() => {
    if (!Array.isArray(modalReviews) || modalReviews.length === 0) return false;
    return modalReviews.every((r) => !!selectedReviewIds?.[String(r?._id)]);
  }, [modalReviews, selectedReviewIds]);

  const anyVisibleSelected = useMemo(() => {
    if (!Array.isArray(modalReviews) || modalReviews.length === 0) return false;
    return modalReviews.some((r) => !!selectedReviewIds?.[String(r?._id)]);
  }, [modalReviews, selectedReviewIds]);

  // Select a color variant and update media/selection
  const handleSelectColorVariant = useCallback((colorName) => {
    if (!productView || !Array.isArray(productView.colorsVariants)) return;
    const variant = productView.colorsVariants.find((color) => color.name === colorName);
    if (!variant) return;

    const resolveSrc = (raw) => {
      if (!raw) return null;
      const s = typeof raw === 'string' ? raw : raw?.src;
      return s ? normalizeImageUrl(s) : null;
    };

    const primarySrc = resolveSrc(variant.src) || resolveSrc(productView.image);
    const extrasSrcs = Array.isArray(variant.images)
      ? variant.images
      : (Array.isArray(variant.imageB) ? variant.imageB : []);

    const mapImages = (arr) => (Array.isArray(arr) ? arr.map(m => ({ type: 'image', src: resolveSrc(m) || '' })).filter(x => !!x.src) : []);
    const fallbackExtras = mapImages(productView?.imageB);
    const newExtras = (Array.isArray(extrasSrcs) && extrasSrcs.length > 0) ? mapImages(extrasSrcs) : fallbackExtras;

    const newMedia = [
      ...(productView?.video ? [{ type: 'video', src: productView.video }] : []),
      ...(primarySrc ? [{ type: 'image', src: primarySrc }] : []),
      ...newExtras,
    ];

    if (primarySrc) setSelectedMedia(primarySrc);
    setProductMedia(newMedia);
    setSelectedColorsVariants(variant.name);
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
  }, [productView]);

  // Prepare product media (images/video)
  const setProductMediaOnLoad = useCallback(() => {
    const newProductMedia = [];

    // Always show product main image first
    if (hasNewVariants) {
      const primary = selectedSku?.image || productView?.image
      if (primary) setSelectedMedia(primary)
    } else {
      if (productView?.image) {
        setSelectedMedia(productView.image);
      }
    }

    if (productView?.video) {
      newProductMedia.push({ type: 'video', src: productView.video });
    }
    if (productView?.imageB && productView.imageB.length > 0) {
      newProductMedia.push(...productView.imageB);
    }

    setProductMedia(newProductMedia);
  }, [productView, hasNewVariants, selectedSku])

  // Media click handler (restored)
  // Legacy click handler no longer needed (gallery component handles selection)

  // Effect A: fetch product once per ID
  useEffect(() => {
    const param = match.params.id
    if (fetchedForIdRef.current === param) return
    fetchedForIdRef.current = param
    const isObjectId = /^[a-f\d]{24}$/i.test(param)
    if (isObjectId) {
      dispatch(listProductDetails(param))
    } else {
      dispatch(listProductDetailsBySlug(param))
    }
    window.scrollTo(0, 0)
  }, [dispatch, match.params.id])

  // Effect A2: reflect store product into productView only if it matches current route param
  useEffect(() => {
    const key = currentKeyRef.current;
    if (!product || !product._id) return;
    const isObjectId = /^[a-f\d]{24}$/i.test(key);
    const matches = isObjectId ? (product._id === key) : (product.slug === key);
    if (matches) setProductView(product);
  }, [product]);

  // Effect B: initialize UI once per loaded (matched) product
  useEffect(() => {
    if (!productView || !productView._id) return
    if (initializedForIdRef.current === productView._id) return
    initializedForIdRef.current = productView._id

    // Start with no variant selected; default to product main image
    setSelectedColorsVariants('');
    setSelectedAttrs({});
    setSelectedSku(null);
    setProductMediaOnLoad();

    if (productView.description) {
      const blocksFromHtml = htmlToDraft(productView.description)
      const { contentBlocks, entityMap } = blocksFromHtml
      const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap)
      setDescriptionState(EditorState.createWithContent(contentState))
    }

    setShowComponent(true)
  }, [productView, handleSelectColorVariant, setProductMediaOnLoad])

  // Effect B2: if current URL uses ObjectId but product has a slug, replace URL to slug path
  useEffect(() => {
    const param = match.params.id
    if (productView?.slug && param && param !== productView.slug) {
      const isObjectId = /^[a-f\d]{24}$/i.test(param)
      if (isObjectId) {
        try {
          history.replace(`/product/${productView.slug}`)
          currentKeyRef.current = productView.slug
        } catch {}
      }
    }
  }, [productView?.slug, match.params.id, history])

  // Effect C: reset review form on success
  useEffect(() => {
    if (successProductReview) {
      setRating(0)
      setComment('')
      dispatch(resetCreateReview())
    }
  }, [successProductReview, dispatch])

  useEffect(() => {
    if (!successAdminReviewOp) return;
    if (!productView?._id) {
      dispatch(resetAdminReviewOp());
      return;
    }

    setEditingReviewId(null);
    setSelectedReviewIds({});
    setNewReviewName('');
    setNewReviewRating(5);
    setNewReviewComment('');
    setNewReviewImages([]);
    setNewReviewVariant('');
    setEditReviewName('');
    setEditReviewRating(5);
    setEditReviewComment('');
    setEditReviewImages([]);
    setEditReviewVariant('');

    dispatch(listProductDetails(productView._id));
    dispatch(resetAdminReviewOp());
  }, [successAdminReviewOp, dispatch, productView?._id]);

  const dismissReviewPurchaseModal = useCallback(() => {
    setShowReviewPurchaseModal(false);
    dispatch(resetCreateReview());
  }, [dispatch]);

  const handleGalleryPhotoClick = useCallback((images, startIndex = 0) => {
    setReviewViewerImages(images);
    setReviewViewerIndex(startIndex);
    setShowReviewImageViewer(true);
  }, []);

  // Cleanup on unmount similar to previous behavior
  useEffect(() => {
    return () => {
      setSelectedMedia([null])
      setProductMedia([])
    }
  }, [])

  useEffect(() => {
    if (hasNewVariants && selectedSku && selectedSku.image) {
      if (userVariantInteracted) {
        setSelectedMedia(selectedSku.image)
      }
    }
  }, [hasNewVariants, selectedSku, userVariantInteracted])

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (showAddedToast) {
      const timer = setTimeout(() => setShowAddedToast(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showAddedToast]);

  const promptVariantSelection = useCallback(() => {
    setShowVariantModal(true)
  }, [])

  // Require variant selection if variants exist and none selected
  const needsVariantSelection =
    Array.isArray(productView?.colorsVariants) &&
    productView.colorsVariants.length > 0 &&
    !selectedColorsVariants;

  const needsNewVariantSelection = hasNewVariants && productView?.variantProps?.some(p => !selectedAttrs || !selectedAttrs[p.id])
  const selectionStatus = hasNewVariants
    ? (needsNewVariantSelection ? 'please select a variant option' : null)
    : (needsVariantSelection ? 'please select a variant option' : null)
  const effectiveStock = hasNewVariants ? (selectedSku ? Number(selectedSku.countInStock || 0) : 0) : Number(productView?.countInStock || 0)
  const buttonDisabled = hasNewVariants
    ? (!selectedSku || isUnavailable || needsNewVariantSelection || effectiveStock <= 0)
    : ((productView?.countInStock === 0) || isUnavailable || needsVariantSelection);

  // Human-readable reason why Add To Cart is disabled (for overlay & tooltip)
  const variantErrorReason = hasNewVariants
    ? (needsNewVariantSelection
        ? 'Select all variant options'
        : (!selectedSku
            ? 'Combination unavailable'
            : (isUnavailable
                ? 'Selected variant unavailable'
                : (effectiveStock <= 0 ? 'Out of stock' : null))))
    : (needsVariantSelection
        ? 'Select a variant'
        : (isUnavailable
            ? 'Selected variant unavailable'
            : (productView?.countInStock === 0 ? 'Out of stock' : null)));

  // Add to cart without redirect - shows toast notification
  const addToCartHandler = async () => {
    if (hasNewVariants ? needsNewVariantSelection : needsVariantSelection) {
      promptVariantSelection();
      return;
    }
    if (isUnavailable) {
      setShowUnavailableModal(true);
      return;
    }
    
    const idForCart = productView?._id || match.params.id;
    const signature = hasNewVariants && selectedAttrs
      ? productView.variantProps.map(p => `${p.id}=${selectedAttrs[p.id] || ''}`).join(';')
      : '';
    
    try {
      await dispatch(addToCart({ 
        id: idForCart, 
        qty, 
        selectedColorsVariants, 
        variantSignature: signature || undefined 
      })).unwrap();
      setShowAddedToast(true);
    } catch (error) {
      console.error('Failed to add to cart:', error);
    }
  };

  // Buy now - adds to cart AND redirects to cart screen
  const buyNowHandler = () => {
    if (hasNewVariants ? needsNewVariantSelection : needsVariantSelection) {
      promptVariantSelection();
      return;
    }
    if (isUnavailable) {
      setShowUnavailableModal(true);
      return;
    }
    
    const idForCart = productView?._id || match.params.id;
    let url = `/cart/${idForCart}?qty=${qty}&selectedColorsVariants=${selectedColorsVariants}`;
    if (hasNewVariants && selectedAttrs) {
      const signature = productView.variantProps.map(p => `${p.id}=${selectedAttrs[p.id] || ''}`).join(';');
      if (signature) {
        url += `&variantSignature=${encodeURIComponent(signature)}`;
      }
    }
    history.push(url);
  };

  const submitHandler = (e) => {
    e.preventDefault();
    const idForReview = productView?._id || match.params.id
    dispatch(createProductReview({ productId: idForReview, review: { rating, comment } }));
  };

  const handleOpenAllReviews = () => {
    setShowAllReviewsModal(true);
  };

  const handleCloseAllReviews = () => {
    setShowAllReviewsModal(false);
    setEditingReviewId(null);
    setSelectedReviewIds({});
    setReviewsRatingFilter('all');
    dispatch(resetAdminReviewOp());
  };

  const toggleReviewSelected = (reviewId, checked) => {
    const id = String(reviewId || '');
    if (!id) return;
    setSelectedReviewIds((prev) => {
      const next = { ...(prev || {}) };
      if (checked) next[id] = true;
      else delete next[id];
      return next;
    });
  };

  const setSelectAllVisible = (checked) => {
    setSelectedReviewIds((prev) => {
      const next = { ...(prev || {}) };
      for (const r of modalReviews) {
        const id = String(r?._id || '');
        if (!id) continue;
        if (checked) next[id] = true;
        else delete next[id];
      }
      return next;
    });
  };

  const selectAllReviews = () => {
    const next = {};
    for (const r of orderedReviews) {
      const id = String(r?._id || '');
      if (id) next[id] = true;
    }
    setSelectedReviewIds(next);
  };

  const clearSelection = () => {
    setSelectedReviewIds({});
  };

  const openReviewImageViewer = (images, startIndex = 0) => {
    const list = Array.isArray(images)
      ? images
        .map((u) => (typeof u === 'string' ? u.trim() : String(u ?? '').trim()))
        .filter(Boolean)
      : [];
    if (list.length === 0) return;
    const idx = Number.isFinite(Number(startIndex)) ? Number(startIndex) : 0;
    const safeIndex = ((idx % list.length) + list.length) % list.length;
    setReviewViewerImages(list);
    setReviewViewerIndex(safeIndex);
    setShowReviewImageViewer(true);
  };

  const closeReviewImageViewer = () => {
    setShowReviewImageViewer(false);
    setReviewViewerImages([]);
    setReviewViewerIndex(0);
  };

  const goPrevReviewImage = useCallback(() => {
    setReviewViewerIndex((prev) => {
      const len = Array.isArray(reviewViewerImages) ? reviewViewerImages.length : 0;
      if (len <= 1) return 0;
      return (prev - 1 + len) % len;
    });
  }, [reviewViewerImages]);

  const goNextReviewImage = useCallback(() => {
    setReviewViewerIndex((prev) => {
      const len = Array.isArray(reviewViewerImages) ? reviewViewerImages.length : 0;
      if (len <= 1) return 0;
      return (prev + 1) % len;
    });
  }, [reviewViewerImages]);

  const handleReviewImageTouchStart = useCallback((e) => {
    if (!showReviewImageViewer) return;
    if (!Array.isArray(reviewViewerImages) || reviewViewerImages.length <= 1) return;
    const t = e?.touches?.[0];
    if (!t) return;
    reviewTouchActiveRef.current = true;
    reviewTouchStartXRef.current = t.clientX;
    reviewTouchStartYRef.current = t.clientY;
  }, [showReviewImageViewer, reviewViewerImages]);

  const handleReviewImageTouchMove = useCallback((e) => {
    if (!reviewTouchActiveRef.current) return;
    const t = e?.touches?.[0];
    const startX = reviewTouchStartXRef.current;
    const startY = reviewTouchStartYRef.current;
    if (!t || typeof startX !== 'number' || typeof startY !== 'number') return;

    // If the user is clearly scrolling vertically, cancel swipe detection.
    const dx = t.clientX - startX;
    const dy = t.clientY - startY;
    if (Math.abs(dy) > 15 && Math.abs(dy) > Math.abs(dx)) {
      reviewTouchActiveRef.current = false;
      reviewTouchStartXRef.current = null;
      reviewTouchStartYRef.current = null;
    }
  }, []);

  const handleReviewImageTouchEnd = useCallback((e) => {
    if (!reviewTouchActiveRef.current) return;
    reviewTouchActiveRef.current = false;

    const t = e?.changedTouches?.[0];
    const startX = reviewTouchStartXRef.current;
    const startY = reviewTouchStartYRef.current;
    reviewTouchStartXRef.current = null;
    reviewTouchStartYRef.current = null;
    if (!t || typeof startX !== 'number' || typeof startY !== 'number') return;

    const dx = t.clientX - startX;
    const dy = t.clientY - startY;

    // Horizontal swipe threshold; ignore if mostly vertical.
    if (Math.abs(dx) < 50) return;
    if (Math.abs(dx) <= Math.abs(dy)) return;

    if (dx < 0) goNextReviewImage();
    else goPrevReviewImage();
  }, [goNextReviewImage, goPrevReviewImage]);

  const beginEditReview = (review) => {
    const id = String(review?._id || '');
    if (!id) return;
    setEditingReviewId(id);
    setEditReviewName(String(review?.name || ''));
    setEditReviewRating(Number(review?.rating || 5));
    setEditReviewComment(String(review?.comment || ''));
    setEditReviewImages(Array.isArray(review?.images) ? [...review.images] : []);
    setEditReviewVariant(String(review?.variant || ''));
  };

  const cancelEditReview = () => {
    setEditingReviewId(null);
    setEditReviewName('');
    setEditReviewRating(5);
    setEditReviewComment('');
    setEditReviewImages([]);
    setEditReviewVariant('');
  };

  const handleAdminCreateReview = async (e) => {
    e?.preventDefault?.();
    if (!productView?._id) return;
    await dispatch(adminCreateProductReview({
      productId: productView._id,
      review: { 
        name: newReviewName, 
        rating: newReviewRating, 
        comment: newReviewComment,
        images: newReviewImages.filter(Boolean),
        variant: newReviewVariant.trim() || undefined,
      },
    }));
  };

  const handleAdminSaveReview = async (reviewId) => {
    if (!productView?._id) return;
    const rid = String(reviewId || '');
    if (!rid) return;
    await dispatch(adminUpdateProductReview({
      productId: productView._id,
      reviewId: rid,
      review: { 
        name: editReviewName, 
        rating: editReviewRating, 
        comment: editReviewComment,
        images: editReviewImages.filter(Boolean),
        variant: editReviewVariant.trim() || undefined,
      },
    }));
  };

  const handleAdminDeleteReview = async (reviewId) => {
    if (!productView?._id) return;
    const rid = String(reviewId || '');
    if (!rid) return;
    await dispatch(adminDeleteProductReview({ productId: productView._id, reviewId: rid }));
  };

  const handleAdminDeleteSelected = async () => {
    if (!productView?._id) return;
    if (!selectedIds.length) return;
    await dispatch(adminBulkDeleteProductReviews({ productId: productView._id, reviewIds: selectedIds }));
  };

  // Image management helpers for admin review editing
  const addEditImageUrl = () => {
    setEditReviewImages((prev) => [...prev, '']);
  };

  const updateEditImageUrl = (index, value) => {
    setEditReviewImages((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const removeEditImageUrl = (index) => {
    setEditReviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const addNewImageUrl = () => {
    setNewReviewImages((prev) => [...prev, '']);
  };

  const updateNewImageUrl = (index, value) => {
    setNewReviewImages((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const removeNewImageUrl = (index) => {
    setNewReviewImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Shared purchase card renderer (no modals; responsive visibility handled at call site)
  const renderPurchaseCard = () => (
    <Card className='product-purchase-card'>
      <ListGroup variant="flush">
        <ListGroup.Item>
          <Row>
            <Col>Price:</Col>
            <Col><strong>${formattedPrice}</strong></Col>
          </Row>
        </ListGroup.Item>
        <ListGroup.Item>
          <Row>
            <Col>Shipping Cost:</Col>
            <Col><h5>Free</h5></Col>
          </Row>
        </ListGroup.Item>
        <ListGroup.Item>
          <Row>
            <Col>Shipping Info:</Col>
            <Col><h6>{productView.shippingMethod}</h6></Col>
          </Row>
        </ListGroup.Item>
        <ListGroup.Item>
          <Row>
            <Col>Returns Policy:</Col>
            <Col><h6>{productView.returnsPolicy}</h6></Col>
          </Row>
        </ListGroup.Item>
        <ListGroup.Item>
          <Row>
            <Col>Status:</Col>
            <Col>{selectionStatus || ((hasNewVariants ? ((selectedSku ? (selectedSku.countInStock || 0) : 0) > 0 && !isUnavailable) : (productView.countInStock > 0)) ? 'In Stock' : 'Out Of Stock')}</Col>
          </Row>
        </ListGroup.Item>
        {(hasNewVariants ? ((selectedSku ? (selectedSku.countInStock || 0) : 0) > 0 && !isUnavailable) : (productView.countInStock > 0)) && (
          <ListGroup.Item>
            <Row>
              <Col>Qty</Col>
              <Col style={{ minWidth: 0 }}>
                <Form.Control
                  as="select"
                  className="w-100 product-select text-truncate"
                  value={qty}
                  onChange={(e) => setQty(Number(e.target.value))}
                >
                  {[...Array(hasNewVariants ? (selectedSku ? (selectedSku.countInStock || 0) : 0) : productView.countInStock).keys()].map((x) => (
                    <option key={x + 1} value={x + 1}>{x + 1}</option>
                  ))}
                </Form.Control>
              </Col>
            </Row>
          </ListGroup.Item>
        )}
        {isUnavailable && (
          <ListGroup.Item>
            <Message variant="warning">This variant isn’t available and cannot be added to your cart.</Message>
          </ListGroup.Item>
        )}
        <ListGroup.Item>
          <div style={{ position: 'relative' }}>
            <Button
              onClick={addToCartHandler}
              className="btn-block mb-2"
              style={{ width: '100%' }}
              type="button"
              disabled={buttonDisabled}
              title={variantErrorReason || undefined}
            >
              Add To Cart
            </Button>
            {(buttonDisabled && variantErrorReason) && (
              <div
                onClick={() => {
                  if (needsVariantSelection || needsNewVariantSelection) { promptVariantSelection() }
                  else if (isUnavailable || (!selectedSku && hasNewVariants && !needsNewVariantSelection)) { setShowUnavailableModal(true) }
                }}
                role="button"
                aria-label={variantErrorReason}
                style={{
                  position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
                  background: 'transparent', cursor: 'pointer', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: '.75rem',
                  fontWeight: 600, color: '#555',
                }}
              />
            )}
          </div>
          <Button
            onClick={buyNowHandler}
            className="btn-block"
            variant="success"
            style={{ width: '100%' }}
            type="button"
            disabled={buttonDisabled}
            title={variantErrorReason || undefined}
          >
            Buy Now
          </Button>
        </ListGroup.Item>
      </ListGroup>
    </Card>
  );
 
  // function showComponentn() {
  //   if(welcome){
  //   setShowComponent(true)
  // }
  // }

  return showComponent && productView ? (
    <>
      <Link className='btn btn-light my-1' to='/'>
        To Gallery
      </Link>
    {userInfo?.isAdmin && productView?._id && (
        <Link
      to={`/admin/product/${productView._id}/edit`}
          className='btn btn-outline-secondary my-1 ms-2'
          aria-label='Edit product'
        >
          <i className='fas fa-edit me-1' /> Edit
        </Link>
      )}
      {userInfo?.isAdmin && safePurchaseLink && (
        <a
          href={safePurchaseLink}
          className='btn btn-outline-primary my-1 ms-2'
          target='_blank'
          rel='noopener noreferrer'
          aria-label='Open purchase link'
        >
          <i className='fas fa-external-link-alt me-1' /> Purchase Link
        </a>
      )}
      {loading ? (
        <Loader  />
      ) : error ? (
        <Message  variant='danger'>
          {error}
        </Message>
      ) : (
        <>
          <div >
            {(() => {
              const origin = (typeof window !== 'undefined' && window.location?.origin) || process.env.REACT_APP_SITE_URL || 'https://lotustor.com'
              const canonical = `${origin}/product/${productView.slug || productView._id}`
              const mainImage = (typeof selectedMedia === 'object' && selectedMedia?.src) ? selectedMedia.src : (productView?.image || '')
              const rawDesc = (productView?.description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
              const shortDesc = rawDesc.slice(0, 160)
              const og = { title: productView.name, description: shortDesc, url: canonical, image: mainImage, type: 'product' }
              const twitter = { title: productView.name, description: shortDesc, image: mainImage, card: 'summary_large_image' }
              const productSchema = {
                '@context': 'https://schema.org',
                '@type': 'Product',
                name: productView.name,
                description: shortDesc,
                sku: productView._id,
                image: mainImage ? [mainImage] : undefined,
                brand: productView.brand ? { '@type': 'Brand', name: productView.brand } : undefined,
                offers: {
                  '@type': 'Offer',
                  priceCurrency: 'USD',
                  price: Number.isFinite(+formattedPrice) ? Number(formattedPrice).toFixed(2) : undefined,
                  availability: productView.countInStock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
                  url: canonical,
                },
                aggregateRating: (productView?.numReviews > 0) ? {
                  '@type': 'AggregateRating',
                  ratingValue: Number(productView.rating || 0),
                  reviewCount: Number(productView.numReviews || 0)
                } : undefined,
              }
              const breadcrumb = {
                '@context': 'https://schema.org',
                '@type': 'BreadcrumbList',
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    name: 'Home',
                    item: origin,
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: productView.name,
                    item: canonical,
                  },
                ],
              }
              // react-helmet assigns <script> children via innerHTML, not textContent,
              // so a raw JSON.stringify (which never escapes "<") lets a value like
              // `</script><img onerror=...>` in productView.name/description close the
              // tag early and inject a real, executing element. Escaping "<"/">"/"&" as
              // unicode escapes prevents that while producing byte-identical JSON once parsed.
              const safeJsonLd = (obj) =>
                JSON.stringify(obj).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026')
              return (
                <>
                  <Meta title={productView.name} description={shortDesc} canonical={canonical} og={og} twitter={twitter} />
                  <Helmet>
                    <script type='application/ld+json'>{safeJsonLd(productSchema)}</script>
                    <script type='application/ld+json'>{safeJsonLd(breadcrumb)}</script>
                  </Helmet>
                </>
              )
            })()}

            <Row>
              <Col md={6}>
                {(() => {
                  const variant = (!hasNewVariants && Array.isArray(productView?.colorsVariants))
                    ? productView.colorsVariants.find((v) => v?.name === selectedColorsVariants)
                    : null;

                  const resolveSrc = (raw) => {
                    if (!raw) return null;
                    const s = typeof raw === 'string' ? raw : raw?.src;
                    return s ? normalizeImageUrl(s) : null;
                  };

                  const primarySrc = hasNewVariants
                    ? (userVariantInteracted
                        ? (resolveSrc(selectedSku?.image) || resolveSrc(productView?.image))
                        : resolveSrc(productView?.image))
                    : (resolveSrc(variant?.src) || resolveSrc(productView?.image));

                  const variantExtras = Array.isArray(variant?.images)
                    ? variant.images
                    : (Array.isArray(variant?.imageB) ? variant.imageB : []);

                  const mapImages = (arr) => (
                    Array.isArray(arr)
                      ? arr.map((m) => ({ type: 'image', src: resolveSrc(m) || '' })).filter((x) => !!x.src)
                      : []
                  );

                  const productExtras = mapImages(productView?.imageB);

                  const extraImages = variant
                    ? (Array.isArray(variantExtras) && variantExtras.length > 0
                        ? mapImages(variantExtras)
                        : productExtras)
                    : productExtras;

                  const videoItems = productView?.video ? [{ type: 'video', src: productView.video }] : [];
                  const imageItems = [
                    ...(primarySrc ? [{ type: 'image', src: primarySrc, alt: productView?.name }] : []),
                    ...extraImages,
                  ];
                  const galleryItems = [...videoItems, ...imageItems];
                  const initialIndex = videoItems.length > 0 ? 1 : 0;

                  return (
                    <ProductMediaGallery
                      key={
                        hasNewVariants
                          ? (selectedSku
                              ? `sku-${Object.values(selectedSku.key || {}).join('_')}-${selectedSku.image || ''}`
                              : 'sku-default')
                          : (selectedColorsVariants || 'legacy-default')
                      }
                      items={galleryItems}
                      initialIndex={initialIndex}
                      onSelect={(item) => setSelectedMedia(item)}
                    />
                  );
                })()}
                <div className="mt-3">
                  {hasNewVariants && (
                    <ListGroup.Item>
                      <VariantPicker
                        variantProps={productView.variantProps}
                        skus={productView.skus}
                        value={selectedAttrs}
                        onChange={({ selected, selectedSku: sku }) => {
                          // Mark that user has interacted (enables image swapping)
                          setUserVariantInteracted(true);
                          const nextSelected = selected || {}
                          const prevSelected = selectedAttrs || {}
                          const imagePropIds = Array.isArray(productView?.variantProps)
                            ? productView.variantProps.filter(p => p && p.type === 'image').map(p => p.id)
                            : []
                          const colorChanged = imagePropIds.some(id => (prevSelected[id] || '') !== (nextSelected[id] || ''))

                          setSelectedAttrs(nextSelected)
                          setSelectedSku(sku || null)
                          // Determine image: prefer SKU image else selected image property value image
                          let newImage = (sku && sku.image) ? sku.image : null
                          if (!newImage && imagePropIds.length > 0) {
                            for (const pid of imagePropIds) {
                              const valId = nextSelected[pid]
                              if (!valId) continue
                              const prop = productView.variantProps.find(p => p.id === pid)
                              const valObj = prop?.values?.find(v => v.id === valId)
                              if (valObj?.image) { newImage = valObj.image; break }
                            }
                          }
                          if (userVariantInteracted && newImage) setSelectedMedia(newImage)
                          if (colorChanged) {
                            try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
                          }
                        }}
                      />
                    </ListGroup.Item>
                  )}

                  {!hasNewVariants && productView.colorsVariants && productView.colorsVariants.length > 0 && (
                    <ListGroup.Item>
                      <Row>
                        <Col>Color/Variants</Col>
                        <Col style={{ minWidth: 0 }}>
                          <Form.Control
                            as="select"
                            className="w-100 product-select text-truncate"
                            value={selectedColorsVariants}
                            title={selectedColorsVariants}
                            onChange={(e) => {setSelectedColorsVariants(e.target.value); handleSelectColorVariant(e.target.value);}}
                          >
                            <option value='' disabled>Select a variant</option>
                            {productView.colorsVariants.map((color) => (
                              <option
                                key={color._id || color.name}
                                value={color.name}
                                title={color.name}
                              >
                                {color.name}{color.unavailable ? ' — Unavailable' : ''}
                              </option>
                            ))}
                          </Form.Control>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  )}
                </div>
                {/* Mobile purchase card (inline) */}
                <div className='d-block d-md-none mt-3'>
                  {renderPurchaseCard()}
                </div>
                <div className="mt-3">
                  <ListGroup variant='flush'>
                    <ListGroup.Item>
                      <h3>{productView.name}</h3>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={scrollToReviews}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            scrollToReviews();
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                        aria-label="Scroll to reviews"
                      >
                        <Rating value={productView.rating} text={`${productView.numReviews} reviews`} />
                      </div>
                    </ListGroup.Item>
                    <ListGroup.Item>Price: ${formattedPrice}</ListGroup.Item>
                    <ListGroup.Item>
                      Description: <Editor editorState={descriptionState} readOnly={true} />
                    </ListGroup.Item>

                    <ListGroup.Item>
                      <Row>
                        <Col>Brand:</Col>
                        <Col>{productView.brand}</Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row>
                        <Col>Category:</Col>
                        <Col>{productView.category}</Col>
                      </Row>
                    </ListGroup.Item>
                  </ListGroup>
                </div>
              </Col>

              <Col xs={12} md={6} lg={5} xl={4} className="product-sticky-col d-none d-md-block" style={{ marginLeft: 'auto' }}>
                {renderPurchaseCard()}
              </Col>
            </Row>

            {/* Unified modals rendered once */}
            <Modal
              show={showVariantModal}
              onHide={() => setShowVariantModal(false)}
              centered
              backdrop="static"
              keyboard
            >
              <Modal.Header closeButton>
                <Modal.Title>Choose a variant</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                Please select a color/variant before adding to your cart.
              </Modal.Body>
              <Modal.Footer>
                <Button variant="primary" onClick={() => setShowVariantModal(false)}>OK</Button>
              </Modal.Footer>
            </Modal>
            <Modal
              show={showUnavailableModal}
              onHide={() => setShowUnavailableModal(false)}
              centered
              backdrop="static"
              keyboard
            >
              <Modal.Header closeButton>
                <Modal.Title>Variant Unavailable</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                The selected variant is currently unavailable. You can preview its images, but it cannot be added to your cart.
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowUnavailableModal(false)}>Close</Button>
              </Modal.Footer>
            </Modal>

            <Modal
              show={showReviewPurchaseModal}
              onHide={dismissReviewPurchaseModal}
              centered
              backdrop
              keyboard
            >
              <Modal.Header closeButton>
                <Modal.Title>Purchase Required</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {REVIEW_PURCHASE_REQUIRED}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="primary" onClick={dismissReviewPurchaseModal}>OK</Button>
              </Modal.Footer>
            </Modal>

            <Modal
              show={showAllReviewsModal}
              onHide={handleCloseAllReviews}
              centered
              size='lg'
              backdrop
              keyboard
            >
              <Modal.Header closeButton>
                <Modal.Title>All Reviews</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <Form.Group controlId='reviewsRatingFilter'>
                    <Form.Label>Filter by rating</Form.Label>
                    <Form.Control
                      as='select'
                      value={reviewsRatingFilter}
                      onChange={(e) => setReviewsRatingFilter(e.target.value)}
                    >
                      <option value='all'>All</option>
                      <option value='5'>5</option>
                      <option value='4'>4</option>
                      <option value='3'>3</option>
                      <option value='2'>2</option>
                      <option value='1'>1</option>
                    </Form.Control>
                  </Form.Group>
                </Form>

                {isAdmin && (
                  <div className='mt-3'>
                    <h5>Admin Review Tools</h5>
                    {errorAdminReviewOp && <Message variant='danger'>{String(errorAdminReviewOp)}</Message>}
                    <Row className='align-items-center'>
                      <Col md='auto'>
                        <Form.Check
                          id='selectAllVisibleReviews'
                          type='checkbox'
                          label='Select all visible'
                          checked={allVisibleSelected}
                          ref={(el) => {
                            if (el && el.input) {
                              el.input.indeterminate = !allVisibleSelected && anyVisibleSelected;
                            }
                          }}
                          onChange={(e) => setSelectAllVisible(!!e.target.checked)}
                          disabled={loadingAdminReviewOp}
                        />
                      </Col>
                      <Col md='auto'>
                        <Button
                          variant='secondary'
                          onClick={selectAllReviews}
                          disabled={loadingAdminReviewOp || orderedReviews.length === 0}
                        >
                          Select all reviews
                        </Button>
                      </Col>
                      <Col md='auto'>
                        <Button
                          variant='outline-secondary'
                          onClick={clearSelection}
                          disabled={loadingAdminReviewOp || selectedIds.length === 0}
                        >
                          Clear selection
                        </Button>
                      </Col>
                      <Col md='auto'>
                        <Button
                          variant='danger'
                          onClick={handleAdminDeleteSelected}
                          disabled={loadingAdminReviewOp || selectedIds.length === 0}
                        >
                          Delete selected
                        </Button>
                      </Col>
                    </Row>

                    <hr />
                    <h6>Add review</h6>
                    <Form onSubmit={handleAdminCreateReview}>
                      <Form.Group controlId='newReviewName'>
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type='text'
                          value={newReviewName}
                          onChange={(e) => setNewReviewName(e.target.value)}
                          disabled={loadingAdminReviewOp}
                        />
                      </Form.Group>
                      <Form.Group controlId='newReviewRating'>
                        <Form.Label>Rating</Form.Label>
                        <Form.Control
                          as='select'
                          value={newReviewRating}
                          onChange={(e) => setNewReviewRating(Number(e.target.value))}
                          disabled={loadingAdminReviewOp}
                        >
                          <option value='5'>5</option>
                          <option value='4'>4</option>
                          <option value='3'>3</option>
                          <option value='2'>2</option>
                          <option value='1'>1</option>
                        </Form.Control>
                      </Form.Group>
                      <Form.Group controlId='newReviewComment'>
                        <Form.Label>Comment</Form.Label>
                        <Form.Control
                          as='textarea'
                          rows={3}
                          value={newReviewComment}
                          onChange={(e) => setNewReviewComment(e.target.value)}
                          disabled={loadingAdminReviewOp}
                        />
                      </Form.Group>
                      <Form.Group controlId='newReviewVariant' className='mt-2'>
                        <Form.Label>Variant/Color</Form.Label>
                        <Form.Control
                          type='text'
                          placeholder='e.g., Color:Blue Size:M'
                          value={newReviewVariant}
                          onChange={(e) => setNewReviewVariant(e.target.value)}
                          disabled={loadingAdminReviewOp}
                        />
                        <Form.Text className='text-muted'>Optional variant or color information</Form.Text>
                      </Form.Group>
                      <Form.Group controlId='newReviewImages' className='mt-2'>
                        <Form.Label>Images</Form.Label>
                        {newReviewImages.map((url, idx) => (
                          <div key={idx} className='d-flex mb-2' style={{ gap: '8px' }}>
                            <Form.Control
                              type='url'
                              placeholder='https://example.com/image.jpg'
                              value={url}
                              onChange={(e) => updateNewImageUrl(idx, e.target.value)}
                              disabled={loadingAdminReviewOp}
                            />
                            <Button
                              variant='outline-danger'
                              size='sm'
                              onClick={() => removeNewImageUrl(idx)}
                              disabled={loadingAdminReviewOp}
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant='outline-secondary'
                          size='sm'
                          onClick={addNewImageUrl}
                          disabled={loadingAdminReviewOp}
                        >
                          + Add Image URL
                        </Button>
                      </Form.Group>
                      <Button
                        type='submit'
                        variant='primary'
                        disabled={loadingAdminReviewOp}
                        className='mt-2'
                      >
                        Create review
                      </Button>
                    </Form>
                  </div>
                )}

                <hr />
                {modalReviews.length === 0 ? (
                  <Message>No Reviews</Message>
                ) : (
                  <ListGroup variant='flush'>
                    {modalReviews.map((review) => {
                      const rid = String(review?._id || '');
                      const isEditing = isAdmin && editingReviewId === rid;
                      const isSelected = !!selectedReviewIds?.[rid];
                      return (
                        <ListGroup.Item key={rid || review?._id}>
                          <Row>
                            {isAdmin && (
                              <Col xs='auto'>
                                <Form.Check
                                  type='checkbox'
                                  id={`selectReview-${rid}`}
                                  label={`Select review by ${String(review?.name || '')}`}
                                  checked={isSelected}
                                  onChange={(e) => toggleReviewSelected(rid, !!e.target.checked)}
                                  disabled={loadingAdminReviewOp}
                                />
                              </Col>
                            )}
                            <Col>
                              {isEditing ? (
                                <>
                                  <Form.Group controlId={`editReviewName-${rid}`}>
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control
                                      type='text'
                                      value={editReviewName}
                                      onChange={(e) => setEditReviewName(e.target.value)}
                                      disabled={loadingAdminReviewOp}
                                    />
                                  </Form.Group>
                                  <Form.Group controlId={`editReviewRating-${rid}`}>
                                    <Form.Label>Rating</Form.Label>
                                    <Form.Control
                                      as='select'
                                      value={editReviewRating}
                                      onChange={(e) => setEditReviewRating(Number(e.target.value))}
                                      disabled={loadingAdminReviewOp}
                                    >
                                      <option value='5'>5</option>
                                      <option value='4'>4</option>
                                      <option value='3'>3</option>
                                      <option value='2'>2</option>
                                      <option value='1'>1</option>
                                    </Form.Control>
                                  </Form.Group>
                                  <Form.Group controlId={`editReviewComment-${rid}`}>
                                    <Form.Label>Comment</Form.Label>
                                    <Form.Control
                                      as='textarea'
                                      rows={3}
                                      value={editReviewComment}
                                      onChange={(e) => setEditReviewComment(e.target.value)}
                                      disabled={loadingAdminReviewOp}
                                    />
                                  </Form.Group>
                                  <Form.Group controlId={`editReviewVariant-${rid}`} className='mt-2'>
                                    <Form.Label>Variant/Color</Form.Label>
                                    <Form.Control
                                      type='text'
                                      placeholder='e.g., Color:Red Size:L'
                                      value={editReviewVariant}
                                      onChange={(e) => setEditReviewVariant(e.target.value)}
                                      disabled={loadingAdminReviewOp}
                                    />
                                    <Form.Text className='text-muted'>Optional variant or color information</Form.Text>
                                  </Form.Group>
                                  <Form.Group controlId={`editReviewImages-${rid}`} className='mt-2'>
                                    <Form.Label>Images</Form.Label>
                                    {editReviewImages.map((url, idx) => (
                                      <div key={idx} className='d-flex mb-2' style={{ gap: '8px' }}>
                                        <Form.Control
                                          type='url'
                                          placeholder='https://example.com/image.jpg'
                                          value={url}
                                          onChange={(e) => updateEditImageUrl(idx, e.target.value)}
                                          disabled={loadingAdminReviewOp}
                                        />
                                        <Button
                                          variant='outline-danger'
                                          size='sm'
                                          onClick={() => removeEditImageUrl(idx)}
                                          disabled={loadingAdminReviewOp}
                                        >
                                          Remove
                                        </Button>
                                      </div>
                                    ))}
                                    <Button
                                      variant='outline-secondary'
                                      size='sm'
                                      onClick={addEditImageUrl}
                                      disabled={loadingAdminReviewOp}
                                    >
                                      + Add Image URL
                                    </Button>
                                  </Form.Group>
                                  <Button
                                    variant='primary'
                                    onClick={() => handleAdminSaveReview(rid)}
                                    disabled={loadingAdminReviewOp}
                                    className='mt-2'
                                  >
                                    Save
                                  </Button>{' '}
                                  <Button
                                    variant='secondary'
                                    onClick={cancelEditReview}
                                    disabled={loadingAdminReviewOp}
                                  >
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <strong>{review?.name}</strong>
                                  <Rating value={review?.rating} />
                                  <p>{String(review?.createdAt || '').substring(0, 10)}</p>
                                  <p>{review?.comment}</p>
                                  {review?.variant && (
                                    <p className='text-muted small mb-1'>
                                      <strong>Variant:</strong> {review.variant}
                                    </p>
                                  )}
                                  {review?.images && review.images.length > 0 && (
                                    <div className='mb-2'>
                                      <strong className='small'>Images:</strong>
                                      <div className='d-flex mt-1' style={{ gap: '4px', flexWrap: 'wrap' }}>
                                        {review.images.map((img, i) => (
                                          <img
                                            key={i}
                                            src={img}
                                            alt=''
                                            style={{
                                              width: '60px',
                                              height: '60px',
                                              objectFit: 'cover',
                                              borderRadius: '4px',
                                              border: '1px solid #ddd',
                                            }}
                                            role='button'
                                            aria-label='View review image'
                                            onClick={() => openReviewImageViewer(review.images, i)}
                                            onError={(e) => { e.target.style.display = 'none' }}
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {isAdmin && (
                                    <>
                                      <Button
                                        variant='outline-primary'
                                        size='sm'
                                        onClick={() => beginEditReview(review)}
                                        disabled={loadingAdminReviewOp}
                                      >
                                        Edit
                                      </Button>{' '}
                                      <Button
                                        variant='outline-danger'
                                        size='sm'
                                        onClick={() => handleAdminDeleteReview(rid)}
                                        disabled={loadingAdminReviewOp}
                                      >
                                        Delete
                                      </Button>
                                    </>
                                  )}
                                </>
                              )}
                            </Col>
                          </Row>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                )}

                <div
                  style={{
                    position: 'sticky',
                    bottom: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    paddingTop: '0.5rem',
                    paddingBottom: '0.5rem',
                    pointerEvents: 'none',
                    zIndex: 1,
                  }}
                >
                  <Button
                    variant='secondary'
                    size='sm'
                    type='button'
                    onClick={handleCloseAllReviews}
                    aria-label='Close all reviews (floating)'
                    style={{ pointerEvents: 'auto' }}
                  >
                    Close
                  </Button>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button variant='secondary' onClick={handleCloseAllReviews}>Close</Button>
              </Modal.Footer>
            </Modal>

            <Modal
              show={showReviewImageViewer}
              onHide={closeReviewImageViewer}
              size='lg'
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Review Images</Modal.Title>
              </Modal.Header>
              <Modal.Body
                className='text-center'
                onTouchStart={handleReviewImageTouchStart}
                onTouchMove={handleReviewImageTouchMove}
                onTouchEnd={handleReviewImageTouchEnd}
                style={{ touchAction: 'pan-y' }}
              >
                {reviewViewerImages.length > 0 && (
                  // eslint-disable-next-line jsx-a11y/img-redundant-alt -- alt text is a test/query contract (ProductScreen.reviewsModal.test.js), not just display text
                  <img
                    src={reviewViewerImages[Math.min(reviewViewerIndex, reviewViewerImages.length - 1)]}
                    alt='Review image'
                    style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                    onError={(e) => { e.target.style.display = 'none' }}
                  />
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant='secondary'
                  onClick={goPrevReviewImage}
                  disabled={reviewViewerImages.length <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant='secondary'
                  onClick={goNextReviewImage}
                  disabled={reviewViewerImages.length <= 1}
                >
                  Next
                </Button>
                <Button variant='secondary' onClick={closeReviewImageViewer}>Close</Button>
              </Modal.Footer>
            </Modal>

            {/* Details moved into left column above to let sticky overlay them */}

            <Row>
              <Col md={9}>
                <div ref={reviewsSectionRef}>
                  <h2>Reviews</h2>
                  {/* Customer Photo Gallery */}
                  {orderedReviews && orderedReviews.length > 0 && (
                    <CustomerPhotoGallery 
                      reviews={orderedReviews}
                      onPhotoClick={handleGalleryPhotoClick}
                    />
                  )}
                  {orderedReviews.length === 0 && <Message>No Reviews</Message>}
                  <ListGroup variant='flush'>
                    {orderedReviews.slice(0, 7).map((review) => (
                      <ListGroup.Item key={review._id}>
                        <Row>
                          <Col>
                            <strong>{review.name}</strong>
                            <Rating value={review.rating} />
                            <p>{review.createdAt.substring(0, 10)}</p>
                            <p>{review.comment}</p>
                          </Col>
                          {review?.images && review.images.length > 0 && (
                            <Col xs={12} md='auto' className='d-flex justify-content-md-end'>
                              <div className='d-flex' style={{ gap: '8px', flexWrap: 'wrap' }}>
                                {review.images.map((img, i) => (
                                  <img
                                    key={i}
                                    src={img}
                                    alt=''
                                    className='border rounded'
                                    style={{ width: '110px', height: '110px', objectFit: 'cover' }}
                                    role='button'
                                    aria-label='View review image'
                                    onClick={() => openReviewImageViewer(review.images, i)}
                                    onError={(e) => { e.target.style.display = 'none' }}
                                  />
                                ))}
                              </div>
                            </Col>
                          )}
                        </Row>
                      </ListGroup.Item>
                    ))}
                    {orderedReviews.length > 7 && (
                      <ListGroup.Item className='text-center'>
                        <Button variant='secondary' onClick={handleOpenAllReviews}>
                          View More Reviews
                        </Button>
                      </ListGroup.Item>
                    )}
                    <ListGroup.Item>
                      <h2>Write a Customer Review</h2>
                      {successProductReview && (
                            <Message variant='success'>
                              Review submitted successfully
                            </Message>
                          )}
                          {loadingProductReview && <Loader />}
                          {errorProductReview && normalizedReviewError !== REVIEW_PURCHASE_REQUIRED && (
                            <Message variant='danger'>{normalizedReviewError}</Message>
                          )}
                      {userInfo ? (
                        <Form onSubmit={submitHandler}>
                          <Form.Group controlId='rating'>
                            <Form.Label>Rating</Form.Label>
                            <Form.Control
                              as='select'
                              value={rating}
                              onChange={(e) => setRating(e.target.value)}
                            >
                              <option value=''>Select...</option>
                              <option value='1'>1 - Poor</option>
                              <option value='2'>2 - Fair</option>
                              <option value='3'>3 - Good</option>
                              <option value='4'>4 - Very Good</option>
                              <option value='5'>5 - Excellent</option>
                            </Form.Control>
                          </Form.Group>
                          <Form.Group controlId='comment'>
                            <Form.Label>Comment</Form.Label>
                            <Form.Control
                              as='textarea'
                              row='3'
                              value={comment}
                              onChange={(e) => setComment(e.target.value)}
                            ></Form.Control>
                          </Form.Group>
                          <Button
                            disabled={loadingProductReview}
                            type='submit'
                            variant='primary'
                          >
                            Submit
                          </Button>
                        </Form>
                      ) : (
                        <Message>
                          Please <Link to='/login'>sign in</Link> to write a review{' '}
                        </Message>
                      )}
                    </ListGroup.Item>
                  </ListGroup>
                </div>
              </Col>
            </Row>
          </div>
         
        </>
      )}

      {/* Floating Add To Cart Button (Mobile Only) */}
      <Button
        className="floating-add-to-cart-btn d-md-none"
        onClick={addToCartHandler}
        disabled={buttonDisabled && !needsVariantSelection && !needsNewVariantSelection}
        aria-label={variantErrorReason ? `${variantErrorReason}. Add to cart` : 'Add to cart'}
        variant="primary"
        title={variantErrorReason || "Add to cart"}
      >
        <i className="fas fa-cart-plus"></i>
      </Button>

      {/* Toast notification for added to cart */}
      <div
        aria-live='polite'
        aria-atomic='true'
        style={{ position: 'fixed', top: '1rem', right: '1rem', zIndex: 1060 }}
      >
        <div
          role='alert'
          style={{
            display: showAddedToast ? 'block' : 'none',
            backgroundColor: '#28a745',
            color: '#fff',
            padding: '0.75rem 1rem',
            borderRadius: '0.25rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            minWidth: '250px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>✓ Added to cart</span>
            <button
              onClick={() => setShowAddedToast(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#fff',
                fontSize: '1.25rem',
                cursor: 'pointer',
                marginLeft: '1rem',
              }}
            >
              ×
            </button>
          </div>
        </div>
      </div>

    </>
  ) : null;
};

export default ProductScreen;
// {
//   "_id": "668740cde2fd5f108e05bb0b",
//   "src": 
// } and "/uploads\\files-1720139981022.jpg"

// import React, { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
// import { useDispatch, useSelector } from 'react-redux';
// import { Row, Col, Image, ListGroup, Card, Button, Form, Container } from 'react-bootstrap';
// import { Editor, EditorState, ContentState } from 'draft-js';
// import htmlToDraft from 'html-to-draftjs';
// import Rating from '../components/Rating';
// import Message from '../components/Message';
// import Loader from '../components/Loader';
// import Meta from '../components/Meta';
// import {
//   listProductDetails,
//   createProductReview,
//   resetCreateReview
// } from '../slices/productSlice';
// import { isEmpty } from 'lodash';

// const ProductScreen = ({ history, match }) => {
//   const [welcome, setWelcome] = useState(false);
//   const [showComponent, setShowComponent] = useState(false);
//   const [colorsVariantClicked, setColorsVariantClicked] = useState(false);
//   // removed unused isHovered state
//   const [descriptionState, setDescriptionState] = useState(() => EditorState.createEmpty());
//   const [qty, setQty] = useState(1);
//   const [rating, setRating] = useState(0);
//   const [comment, setComment] = useState('');
//   const [selectedMedia, setSelectedMedia] = useState([]);
//   const [productMedia, setProductMedia] = useState([]);
//   const [selectedColorsVariants, setSelectedColorsVariants] = useState([]);

//   const dispatch = useDispatch();

//   // SLICES:

//   const productState = useSelector((state) => state.product);
//   const { loading, error, product, successReview, loadingReview } = productState;

//   const userInfo = useSelector((state) => state.user.userInfo);

//   const handleSelectColorVariant = (colorName) => {
//     const selectedColor = product.colorsVariants.find(color => color.name === colorName);
//     if (selectedColor && colorsVariantClicked) {
//       setSelectedMedia(selectedColor.src);
//       setSelectedColorsVariants(selectedColor.name);
//     }
//   };

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         await dispatch(listProductDetails(match.params.id));

//         if (product.colorsVariants) {
//           const names = product.colorsVariants.map(variant => variant.name);
//           setSelectedColorsVariants(names[0]);
//         }

//         setProductMediaOnLoad();

//         if (product.colorsVariants && product.colorsVariants.length > 0) {
//           handleSelectColorVariant(product.colorsVariants[0].name);
//         }

//         if (isEmpty(product.colorsVariants)) {
//           setSelectedColorsVariants('N/A');
//         }

//         if (product.description) {
//           const blocksFromHtml = htmlToDraft(product.description);
//           const { contentBlocks, entityMap } = blocksFromHtml;
//           const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
//           setDescriptionState(EditorState.createWithContent(contentState));
//         }

//       } catch (error) {
//         console.error('Error fetching product details:', error);
//       }
//     };

//     fetchData();
//     window.scrollTo(0, 0);

//     if (successReview) {
//       setRating(0);
//       setComment('');
//       dispatch(resetCreateReview());
//     }

//     if (!product._id || product._id !== match.params.id) {
//       dispatch(listProductDetails(match.params.id));
//     }

//     if (welcome) {
//       setShowComponent(true);
//     }

//     return () => {
//       setSelectedMedia([null]);
//       setProductMedia([]);
//     };
//   }, [dispatch, match.params.id, successReview, product._id, welcome, showComponent]);

//   const setProductMediaOnLoad = () => {
//     const newProductMedia = [];

//     if (product.imageB && product.imageB.length > 0) {
//       newProductMedia.push(...product.imageB);
//       setSelectedMedia(product.image);
//     }

//     if (product.video) {
//       newProductMedia.push({ type: 'video', src: product.video });
//       setSelectedMedia(product.image);
//     }

//     setProductMedia(newProductMedia);
//     setWelcome(true);
//   };

//   const handleMediaClick = (media) => {
//     if (media.type === 'video') window.scrollTo(0, 0);
//     setSelectedMedia(media);
//   };

//   const addToCartHandler = () => {
//     history.push(`/cart/${match.params.id}?qty=${qty}&selectedColorsVariants=${selectedColorsVariants}`);
//   };

//   const submitHandler = (e) => {
//     e.preventDefault();
//     dispatch(createProductReview({ productId: match.params.id, review: { rating, comment } }));
//   };

//   const isMediaObject = typeof selectedMedia === 'object' && selectedMedia !== null;

//   return showComponent ? (
//     <>
//       <Link className='btn btn-light my-1' to='/'>
//         Go Back
//       </Link>
//       {loading ? (
//         <Loader />
//       ) : error ? (
//         <Message variant='danger'>{error}</Message>
//       ) : (
//         <>
//           <Meta title={product.name} />

//           <Row>
//             <Col md={6}>
//               <Container>
//                 <Row>
//                   <Col>
//                     {isMediaObject && selectedMedia.type === 'video' ? (
//                       <div>
//                         <iframe
//                           className='px-3'
//                           src={selectedMedia.src}
//                           width='300rem'
//                           height='350rem'
//                           allow='autoplay'
//                           title={`product-video-${product._id || 'main'}`}
//                           type='video/mp4'
//                         />
//                       </div>
//                     ) : (
//                       <Image
//                         src={isMediaObject ? selectedMedia.src : selectedMedia}
//                         width='370rem'
//                         height='350rem'
//                         alt={isMediaObject && selectedMedia.alt ? selectedMedia.alt : 'Selected Media'}
//                         fluid
//                       />
//                     )}
//                   </Col>
//                 </Row>
//               </Container>

//               <Row xs={6} md={6}>
//                 {productMedia.map((media, index) => (
//                   <div
//                     key={index}
//                     className={`media-thumbnail mb-2 ${selectedMedia === media ? 'selected' : ''}`}
//                     onClick={() => handleMediaClick(media)}
//                   >
//                     {media.type === 'video' ? (
//                       <i style={{ fontSize: '3rem' }} className='fas fa-solid fa-play py-3 image-thumbnail' />
//                     ) : (
//                       <Image src={media.src} thumbnail />
//                     )}
//                   </div>
//                 ))}
//               </Row>
//             </Col>

//             <Col md={3}>
//               <ListGroup variant='flush'>
//                 <ListGroup.Item>
//                   <h3>{product.name}</h3>
//                 </ListGroup.Item>
//                 <ListGroup.Item>
//                   <Rating value={product.rating} text={`${product.numReviews} reviews`} />
//                 </ListGroup.Item>
//                 <ListGroup.Item>Price: ${displayPrice}</ListGroup.Item>
//                 <ListGroup.Item>
//                   Description:
//                   <div className='product-description'>
//                     <Editor editorState={descriptionState} readOnly={true} toolbarHidden={true} />
//                   </div>
//                 </ListGroup.Item>
//               </ListGroup>
//             </Col>

//             <Col md={3}>
//               <Card>
//                 <ListGroup variant='flush'>
//                   <ListGroup.Item>
//                     <Row>
//                       <Col>Price:</Col>
//                       <Col>
//                         <strong>${product.price}</strong>
//                       </Col>
//                     </Row>
//                   </ListGroup.Item>

//                   <ListGroup.Item>
//                     <Row>
//                       <Col>Shipping Cost:</Col>
//                       <Col>
//                         <h5>Free</h5>
//                       </Col>
//                     </Row>
//                   </ListGroup.Item>

//                   <ListGroup.Item>
//                     <Row>
//                       <Col>Shipping Info:</Col>
//                       <Col>
//                         <h5>{product.shippingMethod}</h5>
//                       </Col>
//                     </Row>
//                   </ListGroup.Item>

//                   <ListGroup.Item>
//                     <Row>
//                       <Col>Status:</Col>
//                       <Col>
//                         {product.countInStock > 0 ? 'In Stock' : 'Out Of Stock'}
//                       </Col>
//                     </Row>
//                   </ListGroup.Item>

//                   {product.countInStock > 0 && (
//                     <ListGroup.Item>
//                       <Row>
//                         <Col>Qty</Col>
//                         <Col>
//                           <Form.Control as='select' value={qty} onChange={(e) => setQty(e.target.value)}>
//                             {[...Array(product.countInStock).keys()].map((x) => (
//                               <option key={x + 1} value={x + 1}>
//                                 {x + 1}
//                               </option>
//                             ))}
//                           </Form.Control>
//                         </Col>
//                       </Row>
//                     </ListGroup.Item>
//                   )}

//                   {product.colorsVariants && product.colorsVariants.length > 0 && (
//                     <ListGroup.Item>
//                       <Row>
//                         <Col>Color/Variants</Col>
//                         <Col>
//                           <Form.Control
//                             as='select'
//                             value={selectedColorsVariants}
//                             onChange={(e) => {
//                               setSelectedColorsVariants(e.target.value);
//                               handleSelectColorVariant(e.target.value);
//                             }}
//                             onClick={(e) => {
//                               handleSelectColorVariant(e.target.value);
//                               setColorsVariantClicked(true);
//                             }}
//                           >
//                             {product.colorsVariants.map((color) => (
//                               <option key={color._id} value={color.name}>
//                                 {color.name}
//                               </option>
//                             ))}
//                           </Form.Control>
//                         </Col>
//                       </Row>
//                     </ListGroup.Item>
//                   )}

//                   <ListGroup.Item className='text-center'>
//                     <Button onClick={addToCartHandler} className='btn-block' type='button' disabled={product.countInStock === 0}>
//                       Add To Cart
//                     </Button>
//                   </ListGroup.Item>
//                 </ListGroup>
//               </Card>
//             </Col>
//           </Row>

//           <Row className='my-3'>
//             <Col md={12}>
//               <ListGroup>
//                 <ListGroup.Item>
//                   <h2>Colors</h2>
//                   <div className='color-variant-container'>
//                     {product.colorsVariants && product.colorsVariants.length > 0 ? (
//                       product.colorsVariants.map((variant, index) => (
//                         <Button key={index} variant='light' className={`color-button ${selectedColorsVariants === variant.name ? 'selected' : ''}`} onClick={() => {setColorsVariantClicked(true); handleSelectColorVariant(variant.name)}}>
//                           <div className='color-display' style={{ backgroundColor: variant.colorCode }}></div>
//                           <span className='visually-hidden'>{variant.name}</span>
//                         </Button>
//                       ))
//                     ) : (
//                       <span>N/A</span>
//                     )}
//                   </div>
//                 </ListGroup.Item>
//               </ListGroup>
//             </Col>
//           </Row>
//         </>
//       )}
//     </>
//   ) : null;
// };

// export default ProductScreen;
